import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import LayoutProvider from '../../../Contexts/LayoutProvider';
import LibModal from '../../../features/LibModal/LibModal';
import { useTemplate } from '../../../features/template/TemplateProvider';
import PageTitle from '../../../utils/PageTitle';
import { mkRelativeUrl } from '../../../utils/URL';
import AddIssueTicketModal from '../../SupportTicket/Modals/AddIssueTicketModal';
import ServiceManagePageHeader from './Components/ServiceManagePageHeader';
import ServiceManagePageHeaderMobile from './Components/ServiceManagePageHeaderMobile';
import ServiceManagePageTable from './Components/ServiceManagePageTable';
import ServiceManagePageTableMobile from './Components/ServiceManagePageTableMobile';
import MakeDoneModal from './Modals/MakeDoneModal';
import MakeImplementModal from './Modals/MakeImplementModal';
import MakeIteration from './Modals/MakeIteration';
import MakeOngoingModal from './Modals/MakeOngoingModal';
import ModalRenewService from './Modals/ModalRenewService';
import ViewServiceActivityLogModal from './Modals/ViewServiceActivityLogModal';
import ServicesManageProvider, { useServiceManage } from './ServicesManageProvider';

function PageCont() {
    const initialState = {
        user: 0,
        service: 0
    };
    const [dataId, setDataId] = useState(initialState);
    // Search Params
    const { modalType, closeModal } = useTemplate();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [queryStr, setQueryStr] = useState({ q: searchParams.get('q') || '' });
    const { handleReload, setParams, serviceManageData, showActivityModal, setShowActivityModal } =
        useServiceManage();

    const { setConfirm } = useTemplate();

    const p = useMemo(
        () => ({
            q: searchParams.get('q') || '',
            status: searchParams.get('status') || '',
            'order-by': searchParams.get('order-by'),
            sort: searchParams.get('sort') || 'desc',
            page: searchParams.get('page')
        }),
        [searchParams]
    );

    const debounceUpdate = useDebouncedCallback((val) => {
        navigate(mkRelativeUrl('/services/manage', { ...p, q: val }));
    }, 400);

    const handleChangeFilterData = (e) => {
        const { name, value } = e.target;
        if (name) {
            if (name === 'q') {
                setQueryStr({ q: value });
                debounceUpdate(value);
            } else {
                navigate(mkRelativeUrl('/services/manage', { ...p, [name]: value }));
            }
        }
    };

    const onChangePage = (page) => {
        navigate(mkRelativeUrl('/services/manage', { ...p, page }));
    };

    const handleSortingData = (column) => {
        navigate(
            mkRelativeUrl('/services/manage', {
                ...p,
                'order-by': column,
                sort: p.sort === 'desc' ? 'asc' : 'desc'
            })
        );
    };
    const handleResetFilter = () => {
        navigate(mkRelativeUrl('/services/manage', {}));
    };

    const handleMakeImplement = (ev, item) => {
        ev.stopPropagation();
        ev.preventDefault();

        setConfirm({
            showButton: false,
            text: <MakeImplementModal data={item} handleReload={handleReload} />
        });
    };
    const handleMakeOngoing = (ev, item) => {
        ev.stopPropagation();
        ev.preventDefault();

        setConfirm({
            showButton: false,
            text: <MakeOngoingModal data={item} handleReload={handleReload} />
        });
    };
    const handleMakeDone = (ev, item) => {
        ev.stopPropagation();
        ev.preventDefault();

        setConfirm({
            showButton: false,
            text: <MakeDoneModal data={item} handleReload={handleReload} />
        });
    };

    const handleMakeIteration = (ev, item) => {
        ev.stopPropagation();
        ev.preventDefault();

        setConfirm({
            showButton: false,
            text: <MakeIteration data={item} handleReload={handleReload} />
        });
    };

    const handleCreateSupportTicket = (ev, userId, serviceId) => {
        ev.stopPropagation();
        ev.preventDefault();
        setDataId({
            user: userId,
            service: serviceId
        });
    };

    const closeModalPage = () => {
        setDataId(initialState);
    };

    useEffect(() => {
        setParams(p);
    }, [p, setParams]);

    return (
        <div className="service_manage_page inner_page_cont">
            <LayoutProvider>
                <ServiceManagePageHeader
                    p={{ ...p, ...queryStr }}
                    filterHandler={handleChangeFilterData}
                    handleReload={handleReload}
                    layout="default"
                />
                <ServiceManagePageHeaderMobile
                    p={{ ...p, ...queryStr }}
                    onChangeHandler={handleChangeFilterData}
                    onResetFilter={handleResetFilter}
                    handleReload={handleReload}
                    layout={['md', 'sm', 'xs', 'xxs']}
                />
            </LayoutProvider>
            <LayoutProvider>
                <ServiceManagePageTable
                    layout="default"
                    data={serviceManageData}
                    sortBy={p.sort}
                    sortedColumn={p['order-by']}
                    sortedHandler={handleSortingData}
                    onChangePage={onChangePage}
                    onMakeImplement={handleMakeImplement}
                    onMakeOngoing={handleMakeOngoing}
                    onMakeDone={handleMakeDone}
                    handleCreateSupportTicket={handleCreateSupportTicket}
                    onMakeIteration={handleMakeIteration}
                    showActivityModal={setShowActivityModal}
                />
                <ServiceManagePageTableMobile
                    layout={['md', 'sm', 'xs', 'xxs']}
                    data={serviceManageData}
                    onMakeImplement={handleMakeImplement}
                    onMakeOngoing={handleMakeOngoing}
                    onMakeDone={handleMakeDone}
                    onMakeIteration={handleMakeIteration}
                    handleCreateSupportTicket={handleCreateSupportTicket}
                    showActivityModal={setShowActivityModal}
                />
            </LayoutProvider>

            {showActivityModal?.show && (
                <LibModal
                    title="Service Activity Log"
                    setOpen={setShowActivityModal}
                    className="md:!w-[900px]"
                >
                    <ViewServiceActivityLogModal data={showActivityModal.data} />
                </LibModal>
            )}
            {modalType === 'RENEW_SERVICE_MODAL' && (
                <LibModal title="Renew Service" setOpen={closeModal} className="md:!w-[500px]">
                    <ModalRenewService />
                </LibModal>
            )}

            {dataId.user && dataId.service ? (
                <LibModal title="Add Issue Ticket" setOpen={closeModalPage}>
                    <AddIssueTicketModal
                        userId={dataId.user}
                        ssId={dataId.service}
                        type="service"
                        closeModal={() => closeModalPage()}
                        handleReload={() => null}
                    />
                </LibModal>
            ) : null}
        </div>
    );
}
function ServicesManagePage() {
    // set page title
    PageTitle(`Services Manage`);

    return (
        <ServicesManageProvider>
            <PageCont />
        </ServicesManageProvider>
    );
}
export default ServicesManagePage;
